import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { CustomPaging, DataTypeProvider, PagingState, SortingState, SelectionState } from '@devexpress/dx-react-grid'
import {
    Grid,
    Table,
    TableHeaderRow,
    PagingPanel,
    TableSelection,
    TableColumnResizing,
    TableColumnVisibility,
} from '@devexpress/dx-react-grid-material-ui'
import { useSnackbar } from 'notistack'
import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Link from '@mui/material/Link'
import { Paper, useMediaQuery } from '@mui/material'
import { deleteNotifybar, getNotifybarById } from 'app/redux/store/features/notifybars'
import IconTypeService from './IconTypeService'
import { formatDateTime } from 'app/services/utils/components/FormatDateTime/FormatDateTime'
import NoDataCellWithLoading from 'app/services/utils/components/NoDataCellWithLoading'
import useJumboTheme from '../../../../../../@jumbo/hooks/useJumboTheme'
import LoadingCircle from 'app/services/utils/components/LoadingCircle/LoadingCircle'

const NotifyGridData = ({ onPageChange, onColumnSort, setCurrentPage, currentPage, handleError }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { showDialog, hideDialog } = useJumboDialog()
    const dateTimeColumns = ['dateStart', 'dateEnd']
    const [sorting, setSorting] = useState([{ columnName: 'heading', direction: 'asc' }])
    const pageSize = 25
    const { notifybars, loading, error, refreshing } = useSelector((state) => state.notifybars)
    const { theme } = useJumboTheme()
    const sm = useMediaQuery(theme.breakpoints.down('sm'))
    const mddown = useMediaQuery(theme.breakpoints.down('md'))
    const md = useMediaQuery(theme.breakpoints.between('md', 'xl'))
    const min = sm || mddown || md
    const [apiCallInProgress, setApiCallInProgress] = useState(false)

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) handleError(error)
        else {
            enqueueSnackbar('Notify bar Bar removed successfully', {
                variant: 'success',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }, [loading])

    const onCurrentPageChange = (pageNumber) => {
        onPageChange(pageNumber)
        setCurrentPage(pageNumber)
    }

    const onSortingChange = (sortOrder) => {
        onColumnSort(sortOrder)
        setSorting(sortOrder)
    }

    const onNavigatingToDetail = (id) => {
        dispatch(getNotifybarById({ id: id }))
        navigate(`/settings/configuration/notify-bar/edit/${id}`, { state: { id: id } })
    }

    const TableRow = ({ tableRow, ...restProps }) => {
        return (
            <TableSelection.Row
                {...restProps}
                onDoubleClick={() => {
                    onNavigatingToDetail(tableRow.row.id)
                }}
            />
        )
    }

    const NoDataCellWithLoadingType = (props) => <NoDataCellWithLoading loading={loading} {...props} />
    const DateTimeTypeProvider = (props) => <DataTypeProvider formatterComponent={formatDateTime} {...props} />

    const renderActionCell = (row) => {
        return (
            <div>
                <IconButton
                    onClick={() => {
                        onNavigatingToDetail(row.id)
                    }}>
                    <EditIcon sx={{ color: 'primary.main', fontSize: '18px' }} />
                </IconButton>
                <IconButton onClick={() => onRemoveNotifyBar(row.id)}>
                    <DeleteIcon sx={{ color: 'primary.main', fontSize: '18px' }} />
                </IconButton>
            </div>
        )
    }

    const renderLinkCell = (row) => {
        return (
            <Link
                variant='text'
                underline='none'
                onClick={() => onNavigatingToDetail(row.id)}
                sx={{
                    '&:hover': {
                        color: 'action.active',
                        cursor: 'pointer',
                    },
                }}>
                {row.heading}
            </Link>
        )
    }

    const onRemoveNotifyBar = async (id) => {
        showDialog({
            variant: 'confirm',
            title: 'This will remove the selected record, do you want to continue?',
            onYes: async () => {
                setApiCallInProgress(true)
                dispatch(deleteNotifybar({ id: id }))
                hideDialog()
            },
            onNo: hideDialog,
        })
    }

    const columns = [
        { name: 'heading', title: 'Heading', getCellValue: renderLinkCell },
        { name: 'subText', title: 'Sub Text' },
        { name: 'sideText', title: 'Side Text' },
        { name: 'iconType', title: 'Icon Type', getCellValue: IconTypeService.getIconTypeInfoData },
        { name: 'orderBy', title: 'Sort Order' },
        { name: 'dateStart', title: 'Start Date' },
        { name: 'dateEnd', title: 'End Date' },
        { name: 'Action', title: 'Action', getCellValue: renderActionCell },
    ]

    const columnWidth = useMemo(
        () => [
            { columnName: 'heading', width: min ? '130px' : '13%', minWidth: '130px', wordWrapEnabled: true },
            { columnName: 'subText', width: min ? '220px' : '22%', minWidth: '220px', wordWrapEnabled: true },
            { columnName: 'sideText', width: min ? '110px' : '11%', minWidth: '110px', wordWrapEnabled: true },
            {
                columnName: 'iconType',
                width: min ? '110px' : '11%',
                minWidth: '110px',
                wordWrapEnabled: true,
                getCellValue: IconTypeService.getIconTypeInfoData,
            },
            {
                columnName: 'orderBy',
                width: min ? '120px' : '11%',
                align: 'center',
                minWidth: '120px',
                wordWrapEnabled: true,
            },
            { columnName: 'dateStart', width: min ? '120px' : '12%', minWidth: '120px', wordWrapEnabled: true },
            { columnName: 'dateEnd', width: min ? '120px' : '12%', minWidth: '120px', wordWrapEnabled: true },
            {
                columnName: 'Action',
                width: min ? '90px' : '8%',
                minWidth: '90px',
                wordWrapEnabled: true,
                align: 'center',
            },
        ],
        [min]
    )

    const [columnWidths, setColumnWidths] = useState(columnWidth)

    useEffect(() => {
        setColumnWidths(columnWidth)
    }, [min])

    const sortingColumnExtensions = [
        { columnName: 'Action', sortingEnabled: false },
        { columnName: 'subText', sortingEnabled: false },
        { columnName: 'sideText', sortingEnabled: false },
    ]

    return (
        <Paper style={{ position: 'relative' }}>
            <Grid rows={notifybars.data} columns={columns}>
                {notifybars.totalCount !== 0 && <LoadingCircle refreshing={refreshing} />}
                <DateTimeTypeProvider for={dateTimeColumns} />
                <SortingState
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    columnSortingEnabled={true}
                    columnExtensions={sortingColumnExtensions}
                />
                <PagingState currentPage={currentPage} onCurrentPageChange={onCurrentPageChange} pageSize={pageSize} />
                <CustomPaging totalCount={notifybars.totalCount} />
                <Table columnExtensions={columnWidths} noDataCellComponent={NoDataCellWithLoadingType} />
                <TableColumnResizing
                    columnWidths={columnWidths}
                    onColumnWidthsChange={setColumnWidths}
                    defaultColumnWidths={columnWidths}
                    resizingMode='nextColumn'
                    columnExtensions={columnWidths}
                />
                <TableColumnVisibility />
                <TableHeaderRow showSortingControls />
                <SelectionState />
                <TableSelection selectByRowClick rowComponent={TableRow} showSelectionColumn={false} />
                <PagingPanel />
            </Grid>
        </Paper>
    )
}

export default NotifyGridData
